import React, { useState, useMemo } from 'react';
import { space } from 'styled-system';
import { styled } from '../../../libs/styled.es.js';
import { ButtonChevron } from '../ButtonChevron/ButtonChevron.es.js';
import { Dropdown } from '../Dropdown/Dropdown.es.js';
import { DropdownToggle } from '../Dropdown/DropdownToggle.es.js';
import { DropdownMenu } from '../Dropdown/DropdownMenu.es.js';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const ListItem = styled.li(({ selected, theme }) => `
    padding: 0.6rem 0.8rem;
    font-size: 14px;
    background-color: ${selected ? "#f7f7f7" : ""};
    color: ${selected ? theme.brand.primary : "inherit"};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &:hover {
      background-color: #f7f7f7;
      color: ${theme.brand.primary};
      cursor: pointer;
    }
  `);
const Text = styled.span`
  font-size: 0.875rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  margin-right: 0.25rem;
  text-align: left;
`;
const Content = styled.ul`
  border-radius: 0.25rem;
  border: 1px solid #e0e0e0;
  list-style-type: none;
  padding: 0;
  margin: 0;
  max-height: 200px;
  overflow-y: auto;
  background-color: white;
  box-shadow: 2px 2px 0.8rem 0 rgba(0, 0, 0, 0.08);
  overflow-y: auto;
`;
const Wrapper = styled.div(space, `
    width: 100%;
`);
const StyledButtonChevron = styled(ButtonChevron)`
  width: 100%;
  display: flex;
  justify-content: space-between;
  padding: 0.5rem;
  background-color: white;
  border-radius: 0.25rem;
  border-color: #e0e0e0;
`;
const StyledDropdown = styled(Dropdown)`
  width: 100%;
  button {
    ${(p) => p.disabled ? `
      background-color: #f8f8fc;
      cursor: not-allowed;` : ""}
    > span:first-child {
      flex: 1;
      text-align: left;
    }
  }
`;
const sameWidthModifier = {
  name: "sameWidth",
  enabled: true,
  phase: "beforeWrite",
  requires: ["computeStyles"],
  fn: ({ state }) => {
    state.styles.popper.width = `${state.rects.reference.width}px`;
  },
  effect: ({ state }) => {
    state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`;
  }
};
const dropdownConfigs = {
  modifiers: [
    {
      name: "offset",
      options: {
        offset: [0, 10]
      }
    }
  ]
};
const Select = (_a) => {
  var _b = _a, {
    title,
    onChange,
    items,
    selectedValue = null,
    variant,
    sameWidth = true,
    disabled = false
  } = _b, props = __objRest(_b, [
    "title",
    "onChange",
    "items",
    "selectedValue",
    "variant",
    "sameWidth",
    "disabled"
  ]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const handleChange = ({ value }) => () => {
    setDropdownOpen(false);
    onChange(value);
  };
  const toggle = () => !disabled && setDropdownOpen((prevState) => !prevState);
  const currentTitle = useMemo(() => {
    if (selectedValue === null) {
      return title;
    }
    const filteredItems = items.filter(({ value }) => value === selectedValue);
    return filteredItems.length > 0 ? filteredItems[0].content : title;
  }, [items, selectedValue, title]);
  return /* @__PURE__ */ React.createElement(Wrapper, __spreadValues({}, props), /* @__PURE__ */ React.createElement(StyledDropdown, {
    isOpen: dropdownOpen,
    disabled,
    toggle
  }, /* @__PURE__ */ React.createElement(DropdownToggle, null, /* @__PURE__ */ React.createElement(StyledButtonChevron, {
    type: "button",
    direction: dropdownOpen ? "up" : "down",
    variant: variant === "invalid" ? "danger" : void 0,
    outline: true
  }, /* @__PURE__ */ React.createElement(Text, null, currentTitle))), /* @__PURE__ */ React.createElement(DropdownMenu, {
    popperConfig: __spreadProps(__spreadValues({}, dropdownConfigs), {
      modifiers: [
        ...dropdownConfigs.modifiers,
        sameWidth && sameWidthModifier
      ]
    })
  }, /* @__PURE__ */ React.createElement(Content, null, items.map(({ content, value }, index) => /* @__PURE__ */ React.createElement(ListItem, {
    onClick: handleChange({ content, value }),
    selected: value === selectedValue,
    key: index
  }, content))))));
};

export { Select };
